import { Link } from 'react-router-dom';
import { useBewerberStatistics } from '../api/bewerber';
import { useBedarfeStatistics } from '../api/bedarfe';
import { useKennenlernenStatistics } from '../api/kennenlernen';
import { BedarfLiveFeed } from '../components/dashboard/BedarfLiveFeed';
import { SchoolCityOverview } from '../components/dashboard/SchoolCityOverview';

interface StatCardProps {
  title: string;
  value: number | string;
  subtitle?: string;
  isLoading?: boolean;
  color?: 'primary' | 'green' | 'purple' | 'orange';
}

function StatCard({ title, value, subtitle, isLoading, color = 'primary' }: StatCardProps) {
  const colorClasses = {
    primary: 'text-primary-600',
    green: 'text-green-600',
    purple: 'text-purple-600',
    orange: 'text-orange-600',
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow">
      <h3 className="text-sm font-medium text-gray-500">{title}</h3>
      {isLoading ? (
        <div className="mt-2 flex items-center">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
        </div>
      ) : (
        <>
          <p className={`mt-2 text-3xl font-bold ${colorClasses[color]}`}>{value}</p>
          {subtitle && <p className="text-sm text-gray-500">{subtitle}</p>}
        </>
      )}
    </div>
  );
}

export default function DashboardPage() {
  const { data: bewerberStats, isLoading: loadingBewerber } = useBewerberStatistics();
  const { data: bedarfeStats, isLoading: loadingBedarfe } = useBedarfeStatistics();
  const { data: kennenlernenStats, isLoading: loadingKennenlernen } = useKennenlernenStatistics();

  // Active bewerber (in the pipeline)
  const activeBewerber = bewerberStats?.data
    ? (bewerberStats.data.byStatus['QUALIFIZIERT'] || 0) +
      (bewerberStats.data.byStatus['EINGELADEN'] || 0) +
      (bewerberStats.data.byStatus['TERMINIERT'] || 0)
    : 0;

  const offeneBedarfe = bedarfeStats?.data?.byStatus['OFFEN'] || 0;
  const geplanteKennenlernen = kennenlernenStats?.data?.upcoming || 0;

  // Schulbegleiter actually in a classroom: everyone in GEWONNEN, those still in
  // "Einstellen" whose Arbeitsvertrag is filed, and those in OFFBOARDING whose
  // stichtage have not passed. The split is shown as the subtitle — the rule is
  // unusual enough to be worth spelling out on the tile itself.
  const eingesetzt = bewerberStats?.data?.eingesetzteSchulbegleiter || 0;
  const gewonnen = bewerberStats?.data?.byStatus['GEWONNEN'] || 0;
  const eingesetztImOffboarding = bewerberStats?.data?.eingesetztImOffboarding || 0;
  const eingesetztImEinstellen = Math.max(eingesetzt - gewonnen - eingesetztImOffboarding, 0);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <div className="flex flex-wrap gap-2">
          <Link
            to="/bewerber/new"
            className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors text-sm"
          >
            + Neuer Bewerber
          </Link>
          <Link
            to="/bedarfe/new"
            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-sm"
          >
            + Neuer Bedarf
          </Link>
          <button
            type="button"
            disabled
            title="Bald verfügbar"
            className="px-4 py-2 bg-purple-600 text-white rounded-md text-sm opacity-50 cursor-not-allowed"
          >
            Kennenlernen verwalten
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Offene Bedarfe"
          value={offeneBedarfe}
          subtitle={bedarfeStats?.data ? `${bedarfeStats.data.total} gesamt` : undefined}
          isLoading={loadingBedarfe}
          color="green"
        />
        <StatCard
          title="Aktive Bewerber"
          value={activeBewerber}
          subtitle={bewerberStats?.data ? `${bewerberStats.data.total} gesamt` : undefined}
          isLoading={loadingBewerber}
          color="primary"
        />
        <StatCard
          title="Geplante Kennenlernen"
          value={geplanteKennenlernen}
          subtitle={kennenlernenStats?.data ? `${kennenlernenStats.data.total} gesamt` : undefined}
          isLoading={loadingKennenlernen}
          color="purple"
        />
        <StatCard
          title="Eingesetzte Schulbegleiter"
          value={eingesetzt}
          subtitle={
            bewerberStats?.data
              ? `${gewonnen} gewonnen · ${eingesetztImEinstellen} im Einstellen` +
                (eingesetztImOffboarding > 0 ? ` · ${eingesetztImOffboarding} im Offboarding` : '')
              : undefined
          }
          isLoading={loadingBewerber}
          color="orange"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <BedarfLiveFeed />
        <SchoolCityOverview />
      </div>
    </div>
  );
}
